/**
 * Panel de inicio: la bandeja de pendientes accionables.
 *
 * ERP-UX.md §7 lo dice sin rodeos: en lugar de un centro de notificaciones
 * genérico, **cada línea lleva a una vista filtrada y tiene una acción**.
 * Una alerta sin acción es ruido con formato, y el ruido enseña a la gente
 * a ignorar la pantalla justo antes de que aparezca la que importaba.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';

interface Alert {
  kind: string;
  severity: string;
  message: string;
  entityType: string;
  entityId: string | null;
  dueDate: string | null;
  action: string;
  articleRef?: string | null;
}

interface AlertsResponse {
  /** Las que se enseñan de una en una. */
  shown: Alert[];
  /** Las que se agrupan porque son muchas del mismo tipo. */
  grouped: Alert[];
}

/**
 * A dónde lleva cada tipo de alerta.
 *
 * Lo que no está aquí se queda sin enlace en lugar de llevar a un listado
 * genérico: mandar a alguien a «todas las facturas» cuando le avisas de
 * una concreta es peor que no enlazar.
 */
const DESTINATION: Record<string, string> = {
  EQUIPMENT_DUE: '/equipos?v=overdue',
  EQUIPMENT_OVERDUE: '/equipos?v=overdue',
  CONTRACT_EXPIRING: '/contratos?v=notice',
  INVOICE_OVERDUE: '/facturas?v=overdue',
  QUOTE_PENDING: '/presupuestos?v=awaiting',
  ANOMALY_OPEN: '/anomalias?v=open',
  ANOMALY_OUT_OF_SERVICE: '/anomalias?v=oos',
  WORKORDER_SLA: '/ordenes?v=breached',
  STOCK_BELOW_MINIMUM: '/existencias?v=low',
  WORKSHOP_READY: '/taller?v=ready',
  SITE_WITHOUT_CONTRACT: '/planificacion?v=nocontract',
};

// Las gravedades que emite `ALERT_SEVERITIES`, no otras.
//
// Antes esta tabla usaba CRITICA/ALTA/MEDIA/BAJA, que no son los valores que
// produce el dominio, así que TODAS las alertas caían en el caso por defecto y
// se pintaban en gris — también las urgentes. El panel avisaba, pero no se
// notaba cuál importaba.
const SEVERITY_TONE: Record<string, string> = {
  URGENTE: 'bad',
  ATENCION: 'warn',
  INFORMATIVA: 'info',
};

export function DashboardScreen(): React.ReactNode {
  const { api, user } = useSession();
  const [alerts, setAlerts] = useState<AlertsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api
      .get<AlertsResponse>('/analytics/alerts')
      .then((data) => {
        if (!cancelled) setAlerts(data);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // Un 403 aquí no es un fallo: es un técnico sin permiso de
        // informes. Se dice como tal en lugar de como error.
        setError(
          caught instanceof ApiError && caught.status === 403
            ? null
            : caught instanceof ApiError
              ? caught.message
              : 'No se han podido cargar los avisos.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const shown = alerts?.shown ?? [];
  const grouped = alerts?.grouped ?? [];

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Pendientes</h1>
        {user && <span className="screen__count">{user.roles.join(' · ')}</span>}
      </header>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      {loading && <p className="table__loading">Cargando avisos…</p>}

      {!loading && shown.length === 0 && grouped.length === 0 && (
        <p className="screen__note">
          Nada pendiente. Si esto sale vacío el primer día, conviene comprobar que los vencimientos
          están calculados: una bandeja vacía y una bandeja sin datos se parecen demasiado.
        </p>
      )}

      {shown.length > 0 && (
        <ul className="alerts">
          {shown.map((alert, index) => {
            const to = DESTINATION[alert.kind];
            return (
              <li className="alerts__item" key={`${alert.kind}-${alert.entityId}-${index}`}>
                <span className={`pill pill--${SEVERITY_TONE[alert.severity] ?? 'off'}`}>
                  {alert.severity}
                </span>
                <div className="alerts__body">
                  <p className="alerts__message">{alert.message}</p>
                  <p className="alerts__meta">
                    {alert.action}
                    {alert.dueDate && ` · vence ${alert.dueDate}`}
                    {alert.articleRef && ` · ${alert.articleRef}`}
                  </p>
                </div>
                {to && (
                  <Link className="alerts__action" to={to}>
                    Ver
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {grouped.length > 0 && (
        <>
          <h2 className="screen__subtitle">Agrupadas</h2>
          <ul className="alerts">
            {grouped.map((alert, index) => {
              const to = DESTINATION[alert.kind];
              return (
                <li className="alerts__item" key={`g-${alert.kind}-${index}`}>
                  <span className={`pill pill--${SEVERITY_TONE[alert.severity] ?? 'off'}`}>
                    {alert.severity}
                  </span>
                  <div className="alerts__body">
                    <p className="alerts__message">{alert.message}</p>
                    <p className="alerts__meta">{alert.action}</p>
                  </div>
                  {to && (
                    <Link className="alerts__action" to={to}>
                      Ver
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
